import { useBottomSheetContext, withBottomSheet } from '@/shared/contexts/BottomSheetContext'
import {
  FilterChips,
  ListSection,
  PageHeader,
  PageHero,
  Screen
} from '@/shared/ui/Layout'
import { Resource } from '@/shared/ui/Resource'
import {
  PageFilterChipsSkeleton,
  PageHeroSkeleton,
  PageListScreenSkeleton
} from '@/shared/ui/Skeleton'

import { DigitalProductPrizeBottomSheet } from './BottomSheet'
import { PrizeListCard } from './components/PrizeListCard'
import {
  PrizesIndicatorBar,
  PrizesIndicatorBarSkeleton
} from './components/PrizesIndicatorBar'
import { PrizesListEmpty } from './components/PrizesListEmpty'
import { usePrizeSheetFromUrl } from './hooks/usePrizeSheetFromUrl'
import { usePrizesListView } from './hooks/usePrizesListView'

export const DigitalProductPrizes = withBottomSheet(({ sheetSearch = {} }) => {
  const { showBottomSheet } = useBottomSheetContext()

  // `/sorteios?concurso=` abre o detalhe: é por onde o aviso de vitória da tela
  // inicial chega até aqui.
  usePrizeSheetFromUrl({ sheetSearch })

  const {
    filter,
    setFilter,
    items,
    visibleFilters,
    groups,
    featuredPrize,
    globalProgress,
    isEmpty,
    isFilteredEmpty,
    isLoading
  } = usePrizesListView()

  const handleShowPrize = prize => {
    showBottomSheet({
      component: DigitalProductPrizeBottomSheet,
      ...prize
    })
  }

  return (
    <Screen>
      <Screen.Context className="mb-4">
        <PageHeader title="Concursos" />

        {isLoading && <PageHeroSkeleton withProgress className="mb-0" />}

        {!isLoading && !isEmpty && (
          <PageHero
            art="/prizes/hero-art.png"
            backgroundImage="/prizes/hero-bg.jpg"
            artFit="contain"
            className="relative z-[1] mb-0"
          >
            <PageHero.Title>Participe dos concursos do evento</PageHero.Title>
            <PageHero.ProgressRow
              layout="stacked"
              value={globalProgress.percent}
              label={`${globalProgress.participatingCount} de ${globalProgress.activeCount} com sua participação`}
            />
          </PageHero>
        )}

        {isLoading && (
          <PrizesIndicatorBarSkeleton className="mt-3" />
        )}

        {!isLoading && !isEmpty && (
          <PrizesIndicatorBar
            className="mt-3"
            stats={{
              activeCount: globalProgress.activeCount,
              participatingCount: globalProgress.participatingCount
            }}
          />
        )}
      </Screen.Context>

      <Screen.Sticky>
        {isLoading && <PageFilterChipsSkeleton />}
        {!isLoading && !isEmpty && (
          <FilterChips
            options={visibleFilters}
            value={filter}
            onChange={setFilter}
            aria-label="Filtrar concursos"
          />
        )}
      </Screen.Sticky>

      <Screen.Content className="pt-4">
        {isLoading && (
          <PageListScreenSkeleton
            itemCount={3}
            aria-label="Carregando concursos"
          />
        )}

        {!isLoading && isEmpty && <PrizesListEmpty />}

        {!isLoading && isFilteredEmpty && (
          <PrizesListEmpty
            filtered
            onClearFilter={() => setFilter('all')}
          />
        )}

        {/* Em "Todos": destaque acima (flag `featured` da API), seções pela ordem
            de prêmios (ganhos primeiro). Substitui cinco listas montadas à mão. */}
        {!isLoading && !isEmpty && !isFilteredEmpty && filter === 'all' && (
          <div className="flex flex-col gap-5">
            {featuredPrize && (
              <Resource.Root type="Document" id={featuredPrize.id}>
                <PrizeListCard
                  prize={featuredPrize}
                  featured
                  onShow={() => handleShowPrize(featuredPrize)}
                />
              </Resource.Root>
            )}

            {groups.map(group => (
              <ListSection
                key={group.status}
                id={`prizes-section-${group.status}`}
                title={group.label}
                count={group.items.length}
                status={group.status}
              >
                {group.items.map(prize => (
                  <Resource.Root key={prize.id} type="Document" id={prize.id}>
                    <PrizeListCard
                      prize={prize}
                      onShow={() => handleShowPrize(prize)}
                    />
                  </Resource.Root>
                ))}
              </ListSection>
            ))}
          </div>
        )}

        {!isLoading && !isEmpty && !isFilteredEmpty && filter !== 'all' && (
          <div className="flex flex-col gap-3">
            {items.map(prize => (
              <Resource.Root key={prize.id} type="Document" id={prize.id}>
                <PrizeListCard
                  prize={prize}
                  onShow={() => handleShowPrize(prize)}
                />
              </Resource.Root>
            ))}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <p
            className="text-center font-semibold text-gray-700"
            style={{
              fontSize: '0.75rem',
              letterSpacing: '0.02em',
              padding: '20px 0 40px'
            }}
          >
            Todos os concursos carregados
          </p>
        )}
      </Screen.Content>
    </Screen>
  )
})
